"use client";

import React, { useState, useMemo, useEffect, useLayoutEffect, useContext } from 'react';
import { getForumTheme } from '../../themes/forumTheme';
import { abstractThemeToConcrete, getThemeOptions } from '../../themes/themeNames';
import moment from 'moment';
import { isEAForum } from '../../lib/instanceSettings';
import { THEME_COOKIE } from '../../lib/cookies/cookies';
import { useCookiesWithConsent } from '../hooks/useCookiesWithConsent';
import stringify from 'json-stringify-deterministic';
import { ThemeContext } from './useTheme';
import { isClient, isServer } from '@/lib/executionEnvironment';
import { useTracking } from '@/lib/analyticsEvents';
import { createStylesContext, regeneratePageStyles, StylesContext, useStyles, type StylesContextType } from '../hooks/useStyles';
import { useServerInsertedHTML } from 'next/navigation';
import { defineStyles, setClientMountedStyles } from '../hooks/defineStyles';
import { useCurrentUser } from '../common/withUser';
import { serverEmbeddedStyles } from '../hooks/serverEmbeddedStyles';

export const ThemeContextProvider = ({children}: {
  children: React.ReactNode,
}) => {
  const [cookies, setCookie, removeCookie] = useCookiesWithConsent([THEME_COOKIE]);
  const themeCookie = cookies[THEME_COOKIE];
  const user = useCurrentUser();

  const initialThemeOptions = getThemeOptions(themeCookie, user);
  const [themeOptions, setThemeOptions] = useState(initialThemeOptions);
  const prefersDarkMode = usePrefersDarkMode();

  useEffect(() => {
    // TODO: Keep this paired with ThemePickerMenu when considering account-synced themes for LW/AF.
    if (isEAForum()) {
      removeCookie(THEME_COOKIE, {path: "/"});
    } else {
      if (stringify(themeOptions) !== themeCookie) {
        setCookie(THEME_COOKIE, stringify(themeOptions), {
          path: "/",
          expires: moment().add(2, 'years').toDate(),
        });
      }
    }
  }, [themeOptions, themeCookie, setCookie, removeCookie]);
  
  const concreteThemeOptions = abstractThemeToConcrete(themeOptions, prefersDarkMode);

  const theme: any = useMemo(() =>
    getForumTheme(concreteThemeOptions),
    [concreteThemeOptions]
  );
  const themeContext = useMemo(() => (
    {theme, abstractThemeOptions: themeOptions, concreteThemeOptions, setThemeOptions}),
    [theme, themeOptions, concreteThemeOptions, setThemeOptions]
  );
  
  const [stylesContext] = useState(() => createStylesContext(theme, themeOptions));
  
  return <ThemeContext.Provider value={themeContext}>
    <FMJssProvider stylesContext={stylesContext}>
      {isClient && <ThemeStylesheetSwapper/>}
      {isServer && <StyleHTMLInjector/>}
      {children}
    </FMJssProvider>
  </ThemeContext.Provider>
}

export const FMJssProvider = ({stylesContext, children}: {
  stylesContext: StylesContextType
  children: React.ReactNode
}) => {
  if (isClient) {
    setClientMountedStyles(stylesContext);
  }
  
  return <StylesContext.Provider value={stylesContext}>
    {children}
  </StylesContext.Provider>
}


const ThemeStylesheetSwapper = () => {
  const themeContext = useContext(ThemeContext)!;
  const stylesContext = useContext(StylesContext)!;
  const abstractThemeOptions = themeContext!.abstractThemeOptions;

  useLayoutEffect(() => {
    if (stringify(abstractThemeOptions) !== stringify(window.themeOptions)) {
      window.themeOptions = abstractThemeOptions;
      updateDocumentBodyThemeClassname(abstractThemeOptions.name);
      regeneratePageStyles(themeContext, stylesContext);
    }
  }, [abstractThemeOptions, themeContext, stylesContext]);
  
  return null;
}

function updateDocumentBodyThemeClassname(themeName: string) {
  document.body.classList.remove("theme-default");
  document.body.classList.remove("theme-auto");
  document.body.classList.remove("theme-dark");
  document.body.classList.add("theme-"+themeName);
}

const StyleHTMLInjector = () => {
  const stylesContext = useContext(StylesContext)!;
  const themeContext = useContext(ThemeContext)!;
  
  useServerInsertedHTML(() => {
    if (stylesContext.stylesAwaitingServerInjection.length > 0) {
      const injectedStyles = serverEmbeddedStyles(themeContext.abstractThemeOptions, stylesContext.stylesAwaitingServerInjection)
      stylesContext.stylesAwaitingServerInjection = [];
      return <script dangerouslySetInnerHTML={{__html: injectedStyles}}/>;
    }
    return null;
  });
  
  return null;
}

type OnFinish = (error?: string | Event) => void;

const addStylesheet = (href: string, id: string, onFinish: OnFinish) => {
  const styleNode = document.createElement("link");
  styleNode.setAttribute("id", id);
  styleNode.setAttribute("rel", "stylesheet");
  styleNode.setAttribute("href", href);
  styleNode.onload = () => {
    onFinish();
  }
  styleNode.onerror = onFinish;
  document.head.appendChild(styleNode);
}


const buildPrefersDarkModeQuery = () =>
  isClient && "matchMedia" in window
    ? window.matchMedia("(prefers-color-scheme: dark)")
    : {
      matches: false,
      addEventListener: () => {},
      removeEventListener: () => {},
    };

export const usePrefersDarkMode = () => {
  const [query] = useState(() => buildPrefersDarkModeQuery());
  const [prefersDarkMode, setPrefersDarkMode] = useState(query.matches);
  const { captureEvent } = useTracking();

  useEffect(() => {
    const handler = ({matches}: MediaQueryListEvent) => {
      setPrefersDarkMode(matches);
      captureEvent("prefersDarkModeChange", {
        prefersDarkMode: matches,
      });
    }
    // Check that query.addEventListener exists before using it, because on
    // some browsers (older iOS Safari) it doesn't.
    if (query.addEventListener) {
      query.addEventListener("change", handler);
      return () => query.removeEventListener("change", handler);
    }
  }, [query, captureEvent]);
  
  return prefersDarkMode;
}

export const devicePrefersDarkMode = () => {
  const query = buildPrefersDarkModeQuery();
  return query?.matches ?? false;
}
